import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Plus, Edit2, Trash2, ArrowLeft, Loader2, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyLocation = {
  name: '',
  address: '',
  date: '',
  time_start: '',
  time_end: '',
  description: ''
};

export const AdminLocations = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState(emptyLocation);
  const [saving, setSaving] = useState(false);

  const fetchLocations = useCallback(async () => {
    try {
      const response = await fetch(`${API}/locations`);
      const data = await response.json();
      setLocations(data);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
      toast.error('Failed to load locations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const url = editingLocation 
        ? `${API}/admin/locations/${editingLocation.location_id}`
        : `${API}/admin/locations`;
      
      const response = await fetch(url, {
        method: editingLocation ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to save location');

      toast.success(editingLocation ? 'Location updated!' : 'Location added!');
      setIsDialogOpen(false);
      setEditingLocation(null);
      setFormData(emptyLocation);
      fetchLocations();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save location');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (locationId) => {
    if (!confirm('Are you sure you want to delete this location?')) return;

    try {
      const response = await fetch(`${API}/admin/locations/${locationId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to delete');

      toast.success('Location deleted!');
      fetchLocations();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete location');
    }
  };

  const openEditDialog = (location) => {
    setEditingLocation(location);
    setFormData({
      name: location.name,
      address: location.address,
      date: location.date,
      time_start: location.time_start,
      time_end: location.time_end,
      description: location.description || ''
    });
    setIsDialogOpen(true);
  };

  const openAddDialog = () => {
    setEditingLocation(null);
    setFormData(emptyLocation);
    setIsDialogOpen(true);
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-locations">
      {/* Header */}
      <header className="bg-black border-b-4 border-[#00FFFF] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/admin" className="flex items-center gap-2 text-white hover:text-[#00FFFF] transition-colors">
              <ArrowLeft size={20} />
              <span className="font-space-grotesk font-semibold">Back to Dashboard</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-3">
            <MapPin className="w-8 h-8 text-[#00FFFF]" />
            <h1 className="font-permanent-marker text-3xl text-black">Manage Locations</h1>
          </div>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <button 
                onClick={openAddDialog}
                className="btn-graffiti-cyan flex items-center gap-2"
                data-testid="add-location-btn"
              >
                <Plus size={20} /> Add Location
              </button>
            </DialogTrigger>
            <DialogContent className="bg-white border-4 border-black rounded-xl max-w-md">
              <DialogHeader>
                <DialogTitle className="font-bangers text-2xl">
                  {editingLocation ? 'Edit Location' : 'Add New Location'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="location-form">
                <div>
                  <label className="block font-space-grotesk font-semibold mb-1">Location Name *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="input-graffiti"
                    placeholder="Central Park Farmers Market"
                    data-testid="input-name"
                  />
                </div>
                <div>
                  <label className="block font-space-grotesk font-semibold mb-1">Address *</label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    required
                    className="input-graffiti"
                    placeholder="123 Main St, City, State"
                    data-testid="input-address"
                  />
                </div>
                <div>
                  <label className="block font-space-grotesk font-semibold mb-1">Date *</label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                    className="input-graffiti"
                    data-testid="input-date"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block font-space-grotesk font-semibold mb-1">Start Time *</label>
                    <Input
                      type="time"
                      value={formData.time_start}
                      onChange={(e) => setFormData({ ...formData, time_start: e.target.value })}
                      required
                      className="input-graffiti"
                      data-testid="input-time-start"
                    />
                  </div>
                  <div>
                    <label className="block font-space-grotesk font-semibold mb-1">End Time *</label>
                    <Input
                      type="time"
                      value={formData.time_end}
                      onChange={(e) => setFormData({ ...formData, time_end: e.target.value })}
                      required
                      className="input-graffiti"
                      data-testid="input-time-end"
                    />
                  </div>
                </div>
                <div>
                  <label className="block font-space-grotesk font-semibold mb-1">Description</label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="input-graffiti"
                    placeholder="Additional details..."
                    data-testid="input-description"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setIsDialogOpen(false)}
                    className="flex-1 px-4 py-2 border-4 border-black rounded-lg font-bangers text-lg hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 btn-graffiti-cyan flex items-center justify-center gap-2"
                    data-testid="save-location-btn"
                  >
                    {saving ? <Loader2 className="animate-spin" size={20} /> : <Save size={20} />}
                    Save
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Locations List */}
        {loading ? (
          <div className="text-center py-20">
            <Loader2 className="w-12 h-12 text-[#00FFFF] animate-spin mx-auto" />
          </div>
        ) : locations.length === 0 ? (
          <div className="text-center py-20">
            <div className="card-graffiti inline-block">
              <MapPin className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="font-space-grotesk text-xl text-gray-600">No locations yet.</p>
              <p className="font-space-grotesk text-gray-500 mt-2">Add your first location!</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {locations.map((location, index) => (
              <motion.div
                key={location.location_id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className="card-graffiti flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                data-testid={`location-item-${index}`}
              >
                <div className="flex-1">
                  <h3 className="font-bangers text-xl text-black">{location.name}</h3>
                  <p className="font-space-grotesk text-gray-600">{location.address}</p>
                  <p className="font-space-grotesk text-sm text-gray-500">
                    {location.date} | {location.time_start} - {location.time_end}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => openEditDialog(location)}
                    className="p-2 bg-[#CCFF00] rounded-lg border-2 border-black hover:scale-105 transition-transform"
                    data-testid={`edit-location-${index}`}
                  >
                    <Edit2 size={20} />
                  </button>
                  <button
                    onClick={() => handleDelete(location.location_id)}
                    className="p-2 bg-red-500 text-white rounded-lg border-2 border-black hover:scale-105 transition-transform"
                    data-testid={`delete-location-${index}`}
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminLocations;
